import { UNIT_TYPES } from '@/constant';
import {
	ForeignDashboardActions,
	ForeignDashboardSelectors,
	ForeignSystemUnitActions,
	ForeignSystemUnitSelectors,
} from '@/stores';
import { MyPageSelectors } from '@/stores/my-page';
import { arrayToJson } from '@/utils';
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { DefaultOptionType } from 'antd/es/select';

import { getColumns } from './config';
import { IProps } from './type';

export const useLogic = (props: IProps) => {
	const { t } = useTranslation(['common', 'contractedKorean']);
	const UNIT_TYPES_JSON = arrayToJson(UNIT_TYPES, 'value');
	const { isSystemRole } = useSelector(MyPageSelectors.selectDataMyPage);

	const dispatch = useDispatch();
	const { id } = props;

	const contractedKoreanBusiness = useSelector(
		isSystemRole
			? ForeignSystemUnitSelectors.selectContractedKoreanBusiness
			: ForeignDashboardSelectors.selectContractedKoreanBusiness
	);

	const fetching = useSelector(
		isSystemRole
			? ForeignSystemUnitSelectors.selectContractedKoreanBusinessFetching
			: ForeignDashboardSelectors.selectContractedKoreanBusinessFetching
	);
	const reduxParams = useSelector(
		isSystemRole
			? ForeignSystemUnitSelectors.selectContractedKoreanBusinessParams
			: ForeignDashboardSelectors.selectContractedKoreanBusinessParams
	);
	const total = useSelector(
		isSystemRole
			? ForeignSystemUnitSelectors.selectContractedKoreanBusinessTotal
			: ForeignDashboardSelectors.selectContractedKoreanBusinessTotal
	);

	const fetchData = useCallback(
		(params?: Partial<typeof reduxParams>) => {
			const page = params?.page || reduxParams.page;
			const pageSize = params?.pageSize || reduxParams.pageSize;
			if (!isSystemRole) {
				dispatch(
					ForeignDashboardActions.getContractedKoreanBusinessListRequest({
						page: page,
						pageSize: pageSize,
					})
				);
			} else if (isSystemRole && id) {
				dispatch(
					ForeignSystemUnitActions.getContractedKoreanBusinessListRequest({
						id: id,
						page: page,
						pageSize: pageSize,
					})
				);
			}
		},
		[id, dispatch, reduxParams, isSystemRole]
	);

	useEffect(() => {
		fetchData({});
	}, [isSystemRole, id, dispatch]);

	const handlePageSizeChange = useCallback(
		(pageSize: DefaultOptionType['value']) => {
			fetchData({
				page: 1,
				pageSize: Number(pageSize),
			});
		},
		[fetchData]
	);

	const handlePageChange = useCallback(
		(page: number) => {
			fetchData({
				page,
			});
		},
		[fetchData]
	);

	const columns = useMemo(
		() => getColumns(t, UNIT_TYPES_JSON, isSystemRole, fetchData),
		[t, isSystemRole, fetchData]
	);

	return {
		columns,
		fetching,
		contractedKoreanBusiness,
		handlePageChange,
		handlePageSizeChange,
		reduxParams,
		total,
		t,
	};
};
